<template>
  <div class="form-group searchable-multi-select">
    <label>{{ $t(label) }}</label>
    <el-select
      v-model="selectedItems"
      ref="select"
      value-key="id"
      multiple
      filterable
      remote
      :collapse-tags="collapseTags"
      reserve-keyword
      :placeholder="placeholder || $t('reports.searchProducts')"
      :remote-method="searchItems"
      :loading="loading"
      class="searchable-multi-select__control"
      @change="clearSearch"
      @visible-change="onVisibleChange">
      <el-option
        v-for="item in mergedItems"
        :key="item.id"
        :label="item.label || item.name"
        :value="item">
      </el-option>
    </el-select>
  </div>
</template>

<script>
import swal from 'sweetalert2'

export default {
  name: 'SearchableMultiSelect',
  props: {
    label: {
      type: String,
      required: true
    },
    url: {
      type: String,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    },
    extraParams: {
      type: Object,
      default: () => ({})
    },
    filterLocally: {
      type: Boolean,
      default: false
    },
    initialLimit: {
      type: Number,
      default: 0
    },
    collapseTags: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      items: [],
      loading: false,
      selectedItems: this.value,
      allCategoriesMap: new Map()
    }
  },
  watch: {
    selectedItems (newVal) {
      this.$emit('input', newVal)
    },
    value (newVal) {
      this.selectedItems = newVal
    },
    extraParams: {
      deep: true,
      handler () {
        if (this.initialLimit > 0) {
          this.fetchItems(this.getCurrentQuery())
        }
      }
    }
  },
  mounted () {
    if (this.initialLimit > 0) {
      this.fetchItems('')
    }
  },
  computed: {
    mergedItems () {
      const map = new Map(this.items.map(item => [item.id, item]))
      this.selectedItems.forEach(item => {
        if (!map.has(item.id)) {
          map.set(item.id, item)
        }
      })
      return Array.from(map.values())
    }
  },
  methods: {
    getCurrentQuery () {
      if (this.$refs.select && this.$refs.select.query) {
        return this.$refs.select.query
      }
      return ''
    },
    onVisibleChange (visible) {
      if (visible && this.initialLimit > 0 && !this.items.length) {
        this.fetchItems('')
      }
    },
    searchItems (query) {
      if (!query && this.initialLimit <= 0) {
        return
      }
      this.fetchItems(query)
    },
    fetchItems (query) {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        search: query || '',
        ...this.extraParams
      }
      if (this.initialLimit > 0 && !query) {
        data.limit = this.initialLimit
      }

      this.loading = true
      this.apiRequest(this.url, 'GET', data, response => {
        if (response.status) {
          response.data.forEach(item => {
            this.allCategoriesMap.set(item.id, item)
          })
          this.items = response.data.map(item => ({ ...item, label: item.label || item.name }))
            .filter(item => !this.filterLocally || String(item.label).toLocaleLowerCase().includes(String(query).toLocaleLowerCase()))
        } else {
          swal(
            '',
            response.errors.id || this.$t('validations.wentWrong'),
            'error'
          )
        }

        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    clearSearch () {
      this.$refs.select.focus()
      if (this.$refs.select) {
        this.$refs.select.query = ''
        this.$refs.select.handleQueryChange('')
      }
    }
  }
}
</script>

<style scoped>
/*
 * Global `.el-input__inner { height: 46px !important }` prevents Element from
 * growing a multi-select as tags wrap, and a previous workaround left tags in
 * normal flow *above* a still-tall input (giant empty blue boxes).
 *
 * Fix: the select root is the single visible field; tags size that field;
 * the input is stretched underneath (absolute) so it never adds a second box.
 */
.searchable-multi-select__control {
  display: block;
  position: relative;
  width: 100%;
  min-height: 46px;
  border: 1px solid #E6EFFB;
  background-color: #E6EFFB;
  border-radius: 6px;
  box-sizing: border-box;
}

.searchable-multi-select >>> .el-select__tags {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  -webkit-transform: none;
  transform: none;
  max-width: calc(100% - 30px) !important;
  width: auto !important;
  margin: 0;
  padding: 5px 8px;
  min-height: 46px;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  z-index: 2;
}

.searchable-multi-select >>> .el-select__tags .el-tag {
  max-width: 100%;
  margin: 2px 4px 2px 0;
}

.searchable-multi-select >>> .el-input {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  height: auto !important;
}

.searchable-multi-select >>> .el-input__inner {
  height: 100% !important;
  min-height: 46px !important;
  line-height: 34px !important;
  border: none !important;
  background-color: transparent !important;
  padding-left: 8px !important;
}

.searchable-multi-select >>> .el-input__suffix {
  display: flex;
  align-items: center;
  height: 100%;
}

.searchable-multi-select >>> .el-input__icon {
  line-height: 46px !important;
}

.searchable-multi-select >>> .el-select__input {
  margin-left: 4px;
  max-width: 100% !important;
}
</style>



// WEBPACK FOOTER //
// src/components/UIComponents/Inputs/SearchableMultiSelect.vue